/**
 * Image Services - Public Interface
 *
 * This is the main entry point for image service functionality.
 */

import {DisService} from './dis-service'
import {BrightcoveService} from './brightcove-service'
import {BrightcovePosterService} from './brightcove-poster-service'

/**
 * Registry of all supported image service classes
 * Order matters - services are tried in this order
 */
const SERVICE_CLASSES = [
    BrightcoveService, // Try Brightcove first (more specific pattern)
    BrightcovePosterService, // our own poster endpoint, never converted to DIS
    DisService // DIS with conversion fallback
]

/**
 * Get service instance for a specific URL
 * Tries each service class in order and returns the first that can handle the URL
 *
 * @param {string} url - Image URL
 * @param {Object} context - Context with sharedLibraryStatic and sharedLibraryDis
 * @returns {ImageService|null} Service instance or null if no service can handle the URL
 *
 * @example
 * const service = getServiceForUrl(url, {sharedLibraryStatic, sharedLibraryDis})
 * if (service) {
 *     const sources = service.generateSources([200, 400, 800])
 *     // Returns: [{type: 'image/webp', srcSet: '...'}, ...]
 * }
 */
export function getServiceForUrl(url, context) {
    for (const ServiceClass of SERVICE_CLASSES) {
        const service = ServiceClass.create(url, context)
        if (service) {
            return service
        }
    }
    return null
}

export {ImageService} from './base-service'
